import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Col, Modal, notification, Row, Typography } from 'antd'

import SpinLoader from '../../../helpers/SpinLoader'
import CardBlock from '../../../helpers/CardBlock/CardBlock'

import {
	getPaymentMethods,
	createSubscription,
	getAvailableSubscriptions,
	cancelSubscription,
	switchToSubscription,
	getSubscriptionsUnitsInfo,
} from '../../../../context/subscription'

import {
	IAvailableSubscriptionType,
	ISubscriptionsUnitsInfo,
	PaymentMethod,
	SubscriptionType,
} from '../../../../context/types'
import './index.less'
import useDevAffiliateScript from '../../../../hooks/useDevAffiliateScript'
import { useChannelContext } from '../../../../context/channel'
import { SubscriptionCard } from './SubscriptionCard'
import SubscriptionsUnits from './SubscriptionsUnits/SubscriptionsUnits'
import { currentBrand } from '../../../../config'
import { BaseType } from 'antd/lib/typography/Base'

const { Title, Text } = Typography

function Subscriptions() {
	const idevCustomRef = useRef<HTMLInputElement>(null)
	const { channel } = useChannelContext()
	const [state, updateState] = React.useState(0)
	const forceUpdate = React.useCallback(() => updateState(Date.now()), [])
	const [loading, setLoading] = useState(false)
	const [unitsInfo, setUnitsInfo] = useState<ISubscriptionsUnitsInfo>()
	const [subscription, setSubscription] = useState<IAvailableSubscriptionType>({
		availableToBuy: [],
		free: [],
		current: [],
	})
	const [selectedCard, setSelectedCard] = useState<PaymentMethod>(null)
	const [selectedItem, setSelectedItem] = useState(-1)

	const channelsText = useMemo(() => Array.from(new Set(subscription.free?.map((v) => v.channel?.name))).join(', '), [
		subscription.free,
	])

	useEffect(() => {
		setLoading(true)
		Promise.all([getAvailableSubscriptions(), getPaymentMethods(), getSubscriptionsUnitsInfo()])
			.then((r) => {
				setSubscription(r[0])
				if (r[1].paymentMethods.length) setSelectedCard(r[1].paymentMethods[0])
				setUnitsInfo(r[2])
			})
			.catch(handleError)
			.finally(() => setLoading(false))
	}, [state])

	useEffect(() => {
		if (!!selectedCard && selectedItem > -1) {
			handleSubscribe(selectedItem)
			setSelectedItem(-1)
		}
	}, [selectedItem, selectedCard])

	useDevAffiliateScript()

	const handleSubscribe = (typeId: number) => {
		if (!selectedCard) {
			notification.error({ message: 'Please add a credit card' })
			return
		}

		setLoading(true)
		createSubscription(typeId, selectedCard.id, null, {
			ip: idevCustomRef.current.value,
		})
			.then(() => {
				notification.success({ message: 'Successful' })
				forceUpdate()
			})
			.catch(handleError)
			.finally(() => setLoading(false))
	}

	const handleSwitchTo = (typeId: number) => {
		setLoading(true)
		switchToSubscription(typeId, selectedCard.id, null, {
			ip: idevCustomRef.current.value,
		})
			.then(() => {
				notification.success({ message: 'Successful' })
				forceUpdate()
			})
			.catch(handleError)
			.finally(() => setLoading(false))
	}

	const handleModalAddPaymentMethod = (paymentMethod: PaymentMethod): void => {
		setSelectedCard(paymentMethod)
	}

	const handleError = (err: any) => {
		notification.error({ message: err.message || 'Error' })
	}
	const cancelUserSubscription = (subscriptionType: SubscriptionType) => {
		const userSubscriptionId = subscriptionType.userSubscription?.[0]?.subscription_id

		if (!userSubscriptionId) {
			notification.success({ message: 'Invalid user subscription id' })
			return
		}

		setLoading(true)

		cancelSubscription(userSubscriptionId)
			.then(() => {
				notification.success({ message: 'Subscription canceled' })

				return getAvailableSubscriptions()
			})
			.then(setSubscription)
			.catch(handleError)
			.finally(() => setLoading(false))
	}
	const handleCancelSubscription = async (subscriptionType: SubscriptionType) => {
		Modal.confirm({
			title: 'Confirmation',
			content: 'Are you sure you want to cancel subscription?',
			onOk: async () => {
				await cancelUserSubscription(subscriptionType)
			},
		})
	}
	const ActionButton = ({ data, switchTo }: { data: SubscriptionType; switchTo: boolean }) => (
		<Button
			type="primary"
			shape="round"
			key="buy"
			className="action-button"
			onClick={() => (switchTo ? handleSwitchTo(data.type_id) : handleSubscribe(data.type_id))}
		>
			{switchTo ? 'Switch To' : 'Get it'}
		</Button>
	)

	return (
		<div className="subscriptions-page">
			<SpinLoader loading={loading}>
				<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Title level={3}>Subscription Plans</Title>
					{unitsInfo?.total !== undefined ? (
						<div style={{ textAlign: 'end', fontSize: '20px' }}>
							Total {currentBrand.creditBased ? 'credits' : 'calls'}: <strong>{unitsInfo.total}</strong>
						</div>
					) : null}
				</div>

				{!!subscription.free.length && !!channel.channels?.length && (
					<div style={{ marginBottom: '30px' }}>
						As a member of {channelsText}, you have access to the following subscriptions when booking calls. You may
						not need to purchase anything else.
						<ul>
							{subscription.free.map((freeSubType) => (
								<li key={freeSubType.type_id}>
									<strong>{freeSubType.name}</strong> - {freeSubType.description}
								</li>
							))}
						</ul>
					</div>
				)}

				<Row gutter={[24, 8]} className="subscription-grid-list">
					{subscription.current.map((v) => (
						<Col xs={24} sm={24} md={12} lg={8} xl={6} key={v.type_id}>
							<SubscriptionCard
								data={v}
								current
								bottomCaption={
									<>
										<Text className="units-amount" type="secondary">
											{v.credit_based ? 'Credits' : 'Calls'} per month:{' '}
											<strong>
												{v.units_per_month} {v.credit_based ? 'credits' : 'calls'}
											</strong>
										</Text>
										<Text
											className="units-amount"
											type={!v.isUnlimited && !v.unitsLeftThisMonth ? 'danger' : ('secondary' as BaseType)}
										>
											Available:{' '}
											<strong>
												{v.isUnlimited ? 'UNLIMITED!' : v.unitsLeftThisMonth} {v.credit_based ? 'credits' : 'calls'}
											</strong>
										</Text>
									</>
								}
								bottomAction={
									<Text type="secondary" key="remove" onClick={() => handleCancelSubscription(v)}>
										Cancel Subscription
									</Text>
								}
							/>
						</Col>
					))}

					{subscription.availableToBuy.map((v) => (
						<Col xs={24} sm={24} md={12} lg={8} xl={6} key={v.type_id}>
							<SubscriptionCard
								data={v}
								actions={[<ActionButton key="action" switchTo={Boolean(subscription.current.length)} data={v} />]}
								bottomCaption={
									<Text>
										{v.credit_based ? 'Credits' : 'Calls'} per month:{' '}
										<b>
											{v.units_per_month > 0 ? v.units_per_month : 'UNLIMITED!'}{' '}
											{!v.units_per_month ? '' : v.credit_based ? 'credits' : 'calls'}
										</b>
									</Text>
								}
							/>
						</Col>
					))}
				</Row>

				{(!!selectedCard || !!subscription.availableToBuy.length) && (
					<>
						<Title level={3} style={{ margin: '40px 0' }}>
							Billing Information
						</Title>
						<CardBlock card={selectedCard} showRemoveCardBtn onAfterSave={handleModalAddPaymentMethod} />
					</>
				)}
				{!!Object.keys(unitsInfo?.units || {}).length && (
					<SubscriptionsUnits subscriptionsUnitsMap={unitsInfo?.units} loading={loading} />
				)}
			</SpinLoader>

			{/* Hidden field for IDevAffiliate service, please don't change id */}
			<input type="hidden" ref={idevCustomRef} id="idev_custom_x21" />
		</div>
	)
}

export default Subscriptions
